import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Ban,
  ChevronLeft,
  ChevronRight,
  Flag,
  Heart,
  Loader2,
  Pencil,
  Save,
  Search,
  Shield,
  Sparkles,
  Trash2,
  Undo2,
  User,
  X,
} from "lucide-react";
import { toast } from "sonner";
import type { AdminComment, AdminCommentUser, CommentUserType } from "../../../../shared/index";
import { COMMENT_AUTHORS } from "../../../../shared/index";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PAGE_SIZE = 50;
const COMMENTS_QUERY = ["comments", "list"] as const;

function describeError(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong";
}

function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ModerationBadge({ status }: { status: AdminComment["moderationStatus"] }) {
  const styles: Record<AdminComment["moderationStatus"], string> = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`rounded-md border px-2 py-0.5 text-[11px] font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

export default function CommentsPage() {
  const [page, setPage] = useState(1);
  const [slugSearch, setSlugSearch] = useState("");
  const [appliedSlug, setAppliedSlug] = useState("");
  const [authorSlug, setAuthorSlug] = useState("");
  const [filterDeleted, setFilterDeleted] = useState(false);
  const [filterReported, setFilterReported] = useState(false);
  const [aiFilter, setAiFilter] = useState<"all" | "only" | "exclude">("all");
  const [selectedUser, setSelectedUser] = useState<{ _id: string; displayName: string } | null>(
    null
  );
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState<AdminCommentUser[]>([]);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const userSearchRef = useRef<HTMLDivElement>(null);

  const queryParams = {
    page,
    limit: PAGE_SIZE,
    postSlug: appliedSlug || undefined,
    userId: selectedUser?._id || undefined,
    authorSlug: authorSlug || undefined,
    deleted: filterDeleted,
    reported: filterReported,
    ai: aiFilter === "all" ? undefined : aiFilter,
  };

  const commentsQuery = useQuery({
    queryKey: [...COMMENTS_QUERY, queryParams],
    queryFn: () => api.comments.list(queryParams),
  });

  useEffect(() => {
    if (userQuery.length < 1) {
      setUserResults([]);
      return;
    }
    const timeout = setTimeout(() => {
      api.comments
        .searchUsers(userQuery)
        .then((res) => {
          setUserResults(res);
          setUserSearchOpen(true);
        })
        .catch(() => setUserResults([]));
    }, 300);
    return () => clearTimeout(timeout);
  }, [userQuery]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userSearchRef.current && !userSearchRef.current.contains(e.target as Node)) {
        setUserSearchOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setAppliedSlug(slugSearch.trim());
    setPage(1);
  }

  function clearSearch() {
    setSlugSearch("");
    setAppliedSlug("");
    setPage(1);
  }

  function clearUserFilter() {
    setSelectedUser(null);
    setUserQuery("");
    setUserResults([]);
    setPage(1);
  }

  const data = commentsQuery.data;
  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <div className="space-y-6">
      <Link
        to="/dashboard/news-portal"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to News Portal
      </Link>

      <header>
        <h1 className="text-2xl font-bold">Comments</h1>
        <p className="text-sm text-muted-foreground">
          {data ? `${data.total} comment${data.total !== 1 ? "s" : ""} found` : "Moderate user comments"}
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
        <form onSubmit={handleSearch} className="flex w-full items-end gap-2 sm:w-auto">
          <div className="grid flex-1 gap-1.5 sm:flex-initial">
            <label className="text-xs font-medium text-muted-foreground">Post Slug</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={slugSearch}
                onChange={(e) => setSlugSearch(e.target.value)}
                placeholder="Search by post slug..."
                className="w-full pl-9 sm:w-72"
              />
            </div>
          </div>
          <Button type="submit" size="sm">
            Search
          </Button>
          {appliedSlug && (
            <Button type="button" variant="ghost" size="sm" onClick={clearSearch}>
              Clear
            </Button>
          )}
        </form>

        {/* User search */}
        <div className="grid w-full gap-1.5 sm:w-auto" ref={userSearchRef}>
          <label className="text-xs font-medium text-muted-foreground">User</label>
          {selectedUser ? (
            <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-sm">
              <User className="h-4 w-4 text-primary" />
              <span className="font-medium text-primary">{selectedUser.displayName}</span>
              <button
                type="button"
                onClick={clearUserFilter}
                className="ml-1 cursor-pointer rounded-full p-0.5 transition-colors hover:bg-primary/20"
              >
                <X className="h-3 w-3 text-primary" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <User className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                onFocus={() => userResults.length > 0 && setUserSearchOpen(true)}
                placeholder="Search by user..."
                className="w-full pl-9 sm:w-64"
              />
              {userSearchOpen && userResults.length > 0 && (
                <div className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border bg-card shadow-lg sm:w-80">
                  {userResults.map((u) => (
                    <button
                      key={u._id}
                      type="button"
                      className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors first:rounded-t-lg last:rounded-b-lg hover:bg-accent"
                      onClick={() => {
                        setSelectedUser({ _id: u._id, displayName: u.displayName });
                        setUserQuery("");
                        setUserResults([]);
                        setUserSearchOpen(false);
                        setPage(1);
                      }}
                    >
                      {u.avatarUrl ? (
                        <img
                          src={u.avatarUrl}
                          alt=""
                          className="h-5 w-5 shrink-0 rounded-full object-cover"
                        />
                      ) : (
                        <User className="h-5 w-5 shrink-0 rounded-full bg-muted p-0.5 text-muted-foreground" />
                      )}
                      <div className="min-w-0 flex-1 text-left">
                        <div className="truncate font-medium">{u.displayName}</div>
                        <div className="truncate font-mono text-xs text-muted-foreground">
                          {u._id}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Author filter */}
        <div className="grid w-full gap-1.5 sm:w-auto">
          <label className="text-xs font-medium text-muted-foreground">Author</label>
          <select
            value={authorSlug}
            onChange={(e) => {
              setAuthorSlug(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border bg-card px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 sm:w-56"
          >
            <option value="">All authors</option>
            {COMMENT_AUTHORS.map((a) => (
              <option key={a.slug} value={a.slug}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3">
          {[
            {
              label: "Deleted",
              icon: Trash2,
              checked: filterDeleted,
              onChange: setFilterDeleted,
              title: "Deleted comments",
            },
            {
              label: "Reported",
              icon: Flag,
              checked: filterReported,
              onChange: setFilterReported,
              title: "Reported comments",
            },
          ].map(({ label, icon: Icon, checked, onChange, title }) => (
            <button
              key={label}
              type="button"
              title={title}
              onClick={() => {
                onChange(!checked);
                setPage(1);
              }}
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                checked
                  ? "border-primary/30 bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:bg-accent"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}

          {/* AI authorship: all / AI-only (incl. replies) / non-AI */}
          <div className="flex items-center gap-0.5 rounded-lg border border-border p-0.5">
            <Sparkles className="ml-1.5 mr-0.5 h-3.5 w-3.5 text-muted-foreground" />
            {(
              [
                { value: "all", label: "All", title: "All comments" },
                {
                  value: "only",
                  label: "AI",
                  title: "AI-authored comments and the replies written to them",
                },
                { value: "exclude", label: "Non-AI", title: "Comments not authored by AI" },
              ] as const
            ).map(({ value, label, title }) => (
              <button
                key={value}
                type="button"
                title={title}
                onClick={() => {
                  setAiFilter(value);
                  setPage(1);
                }}
                className={`cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  aiFilter === value
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      {commentsQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : commentsQuery.isError ? (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load comments: {describeError(commentsQuery.error)}
        </div>
      ) : (
        <>
          <div className="grid gap-3">
            {data?.comments.map((comment) => (
              <CommentRow key={comment._id} comment={comment} />
            ))}
            {data?.comments.length === 0 && (
              <div className="py-16 text-center text-sm text-muted-foreground">No comments found</div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CommentRow({ comment }: { comment: AdminComment }) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [bioDraft, setBioDraft] = useState("");

  // A comment the user deleted themselves stays approved/pending; only
  // moderation-rejected comments are restorable.
  const deletedByUser = comment.isDeleted && comment.moderationStatus !== "rejected";

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: COMMENTS_QUERY });
  }

  const deleteMutation = useMutation({
    mutationFn: () => api.comments.delete(comment._id),
    onSuccess: invalidate,
    onError: (e) => toast.error(describeError(e)),
  });

  const restoreMutation = useMutation({
    mutationFn: () => api.comments.restore(comment._id),
    onSuccess: invalidate,
    onError: (e) => toast.error(describeError(e)),
  });

  const typeMutation = useMutation({
    mutationFn: (next: CommentUserType) =>
      api.comments.setUserType(comment.userId, next, comment.staticUserId),
    onSuccess: () => {
      toast.success("User type updated");
      invalidate();
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const blockMutation = useMutation({
    mutationFn: (next: boolean) =>
      api.comments.setUserBlocked(comment.staticUserId ?? "", next),
    onSuccess: (_data, next) => {
      toast.success(next ? "User blocked" : "User unblocked");
      invalidate();
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const profileMutation = useMutation({
    mutationFn: (fields: { displayName?: string; bio?: string }) =>
      api.comments.setUserProfile(comment.staticUserId ?? "", fields),
    onSuccess: () => {
      toast.success("User profile updated");
      setEditingProfile(false);
      invalidate();
    },
    onError: (e) => toast.error(describeError(e)),
  });

  function handleDelete() {
    if (!confirm("Delete this comment? This will mark it as rejected and deleted.")) return;
    deleteMutation.mutate();
  }

  function handleRestore() {
    if (!confirm("Restore this comment? This will mark it as approved and visible.")) return;
    restoreMutation.mutate();
  }

  function handleToggleBlock() {
    if (!comment.staticUserId) {
      toast.error("This comment has no static user id, so the user can't be blocked.");
      return;
    }
    const next = !comment.userIsBlocked;
    if (
      !confirm(
        `${next ? "Block" : "Unblock"} "${comment.displayName}"? ${
          next ? "They will no longer be able to comment." : "They will be able to comment again."
        }`
      )
    )
      return;
    blockMutation.mutate(next);
  }

  function handleEditProfile() {
    if (!comment.staticUserId) {
      toast.error("This comment has no static user id, so the profile can't be edited.");
      return;
    }
    setNameDraft(comment.displayName ?? "");
    setBioDraft(comment.userBio ?? "");
    setEditingProfile(true);
  }

  function handleSaveProfile() {
    const fields: { displayName?: string; bio?: string } = {};
    if (nameDraft !== (comment.displayName ?? "")) fields.displayName = nameDraft;
    if (bioDraft !== (comment.userBio ?? "")) fields.bio = bioDraft;
    if (fields.displayName === undefined && fields.bio === undefined) return;
    profileMutation.mutate(fields);
  }

  return (
    <div
      className={`rounded-xl bg-card p-4 shadow-sm ring-1 ${
        comment.isDeleted ? "bg-red-50/30 ring-red-200/60" : "ring-black/4"
      }`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          {/* Header */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{comment.displayName}</span>
            {comment.userType === "staff" && (
              <span className="rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary-foreground">
                Staff
              </span>
            )}
            {comment.userType === "ai" && (
              <span className="rounded bg-purple-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                AI
              </span>
            )}
            <ModerationBadge status={comment.moderationStatus} />
            {comment.userIsBlocked && (
              <span className="flex items-center gap-1 rounded-md border border-red-300 bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800">
                <Ban className="h-3 w-3" />
                Blocked
              </span>
            )}
            {comment.isDeleted && (
              <span className="flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                <Trash2 className="h-3 w-3" />
                {deletedByUser ? "Deleted by user" : "Deleted"}
              </span>
            )}
            {comment.reports.length > 0 && (
              <button
                type="button"
                onClick={() => setShowReports(!showReports)}
                className="flex cursor-pointer items-center gap-1 rounded-md border border-orange-200 bg-orange-50 px-2 py-0.5 text-[11px] font-medium text-orange-700 transition-colors hover:bg-orange-100"
              >
                <Flag className="h-3 w-3" />
                {comment.reports.length} report{comment.reports.length !== 1 ? "s" : ""}
              </button>
            )}
          </div>

          {/* Meta */}
          <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span>{formatDate(comment.createdAt)}</span>
            <span className="font-mono">{comment.postSlug}</span>
            {comment.userEmail && <span>{comment.userEmail}</span>}
            {comment.likesCount > 0 && (
              <span className="flex items-center gap-1">
                <Heart className="h-3 w-3" />
                {comment.likesCount}
              </span>
            )}
            {comment.parentId && (
              <span className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                Reply
              </span>
            )}
          </div>

          {/* Content */}
          <div className="mt-2 text-sm leading-relaxed">
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{
                __html:
                  comment.content.length > 200 && !expanded
                    ? comment.content.slice(0, 200) + "..."
                    : comment.content,
              }}
            />
            {comment.content.length > 200 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="mt-1 cursor-pointer text-xs font-medium text-primary hover:underline"
              >
                {expanded ? "Show less" : "Show more"}
              </button>
            )}
          </div>

          {comment.imageUrl && (
            <a
              href={comment.imageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-xs text-primary hover:underline"
            >
              View attached image
            </a>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border pt-3 sm:flex-col sm:items-end sm:border-0 sm:pt-0">
          <span
            className="min-w-0 cursor-pointer select-all truncate font-mono text-[11px] text-muted-foreground"
            title="Comment ID"
          >
            {comment._id}
          </span>
          <div className="flex items-center gap-1">
            <select
              value={comment.userType}
              disabled={typeMutation.isPending || !comment.staticUserId}
              title={!comment.staticUserId ? "No static user id — cannot classify" : "Set user type"}
              onChange={(e) => {
                const next = e.target.value as CommentUserType;
                if (
                  !confirm(
                    `Set "${comment.displayName}" as ${next}? This affects all of their comments.`
                  )
                ) {
                  e.currentTarget.value = comment.userType; // revert the visual selection
                  return;
                }
                typeMutation.mutate(next);
              }}
              className="h-7 rounded border bg-card px-1 text-xs"
            >
              <option value="user">User</option>
              <option value="staff">Staff</option>
              <option value="ai">AI</option>
            </select>
            <Button
              variant="ghost"
              size="icon"
              className={`h-7 w-7 ${
                comment.userIsBlocked
                  ? "text-red-600 hover:text-red-700"
                  : "text-muted-foreground hover:text-red-600"
              }`}
              onClick={handleToggleBlock}
              disabled={blockMutation.isPending || !comment.staticUserId}
              title={
                !comment.staticUserId
                  ? "No static user id — cannot block"
                  : comment.userIsBlocked
                    ? "Unblock user"
                    : "Block user"
              }
            >
              <Ban className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`h-7 w-7 ${
                editingProfile
                  ? "text-primary"
                  : "text-muted-foreground hover:text-primary"
              }`}
              onClick={handleEditProfile}
              disabled={profileMutation.isPending || !comment.staticUserId}
              title={
                !comment.staticUserId
                  ? "No static user id — cannot edit profile"
                  : "Edit user profile"
              }
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            {!comment.isDeleted ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                title="Delete comment"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            ) : deletedByUser ? null : (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-emerald-600 hover:text-emerald-700"
                onClick={handleRestore}
                disabled={restoreMutation.isPending}
                title="Restore comment"
              >
                <Undo2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Reports detail */}
      {showReports && comment.reports.length > 0 && (
        <div className="mt-3 rounded-lg border border-orange-200 bg-orange-50 p-3">
          <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-orange-800">
            <span className="font-semibold">Reported user:</span>
            <span className="font-medium">{comment.displayName || comment.userName}</span>
            {comment.userEmail && <span className="text-orange-600">{comment.userEmail}</span>}
          </div>
          <h4 className="mb-2 text-xs font-semibold text-orange-800">Reports</h4>
          <div className="space-y-1">
            {comment.reports.map((r, i) => (
              <div key={i} className="text-xs text-orange-700">
                <span className="font-medium">{r.displayName || "User"}</span>
                {r.email && <span className="ml-1 text-orange-500">{r.email}</span>}
                <span className="ml-1 font-mono text-orange-500">({r.userId})</span>: {r.reason}{" "}
                <span className="text-orange-500">({formatDate(r.createdAt)})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profile editor */}
      {editingProfile && (
        <div className="mt-3 rounded-lg border bg-muted/30 p-3">
          <div className="grid gap-1.5">
            <label className="text-xs font-medium text-muted-foreground">Display name</label>
            <Input
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              placeholder="Enter display name..."
            />
          </div>
          <div className="mt-3 grid gap-1.5">
            <label className="text-xs font-medium text-muted-foreground">Bio</label>
            <textarea
              value={bioDraft}
              onChange={(e) => setBioDraft(e.target.value)}
              rows={3}
              placeholder="Enter user bio..."
              className="w-full resize-y rounded-md border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingProfile(false)}
              disabled={profileMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSaveProfile}
              disabled={
                profileMutation.isPending ||
                (nameDraft === (comment.displayName ?? "") &&
                  bioDraft === (comment.userBio ?? ""))
              }
            >
              {profileMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
